import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import type { TagGroup } from "../components/filters/types";

interface UseMultiGroupTagFilterOptions {
  /** Initial filter IDs */
  initialFilters?: string[];
  /** Callback when filters change */
  onFilterChange?: (filters: string[]) => void;
  /** Tag groups for computing selected counts */
  tagGroups?: TagGroup[];
}

interface UseMultiGroupTagFilterReturn {
  // State
  /** Currently active filter IDs */
  activeFilters: string[];
  /** Currently open dropdown group ID, or null if none */
  openDropdown: string | null;

  // Actions
  /** Toggle a filter on/off */
  toggleFilter: (filterId: string) => void;
  /** Remove a specific filter */
  removeFilter: (filterId: string) => void;
  /** Clear all active filters */
  clearAllFilters: () => void;
  /** Replace all active filters through the same state/callback path as other mutations */
  setFilters: (filters: string[]) => void;
  /** Set which dropdown is open (null to close all) */
  setOpenDropdown: (groupId: string | null) => void;

  // Computed
  /** Get selected tag IDs for a specific group */
  getSelectedTagsForGroup: (groupId: string) => string[];
  /** Get count of selected tags for a specific group */
  getSelectedCountForGroup: (groupId: string) => number;
}

const EMPTY_FILTERS: string[] = [];

/**
 * True for a parent tag that is NOT itself a filter ID: a `filter-group-*`
 * stands for its children, so its checkbox state is derived from them and
 * toggling it toggles them. Every other parent (a `category-*` primary) IS a
 * real filter ID — the query expands it to its descendants — so it selects and
 * clears on its own row. The rendered checkbox state must use this same test,
 * or a parent can show checked via children and never uncheck from its row.
 */
export function isDerivedParentTagId(tagId: string) {
  return tagId.startsWith("filter-group-");
}

function sameFilterIds(a: string[], b: string[]) {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

interface FilterState {
  value: string[];
  source: string[];
  hasLocalChange: boolean;
}

function resolveActiveFilters(state: FilterState, initialFilters: string[]) {
  if (!sameFilterIds(state.source, initialFilters)) return initialFilters;
  if (state.hasLocalChange && state.source !== initialFilters) return initialFilters;
  return state.value;
}

/**
 * Hook for managing multi-group tag filter state.
 *
 * Manages:
 * - Active filter selection (with parent sync)
 * - Search query for filtering dropdown options
 * - Dropdown open/close state (only one at a time)
 *
 * Filter ID format (for backward compatibility):
 * - Projects: `project-{project_id}`
 * - Categories: `category-{category_name}`
 * - Disciplines: `construction-{discipline_id}`
 * - Stages: `stage-{stage_id}`
 *
 * @example
 * ```tsx
 * const {
 *   activeFilters,
 *   toggleFilter,
 *   clearAllFilters,
 *   getSelectedCountForGroup,
 * } = useMultiGroupTagFilter({
 *   initialFilters: [],
 *   onFilterChange: (filters) => console.log(filters),
 *   tagGroups: groups,
 * });
 * ```
 */
export function useMultiGroupTagFilter({
  initialFilters = EMPTY_FILTERS,
  onFilterChange,
  tagGroups = [],
}: UseMultiGroupTagFilterOptions): UseMultiGroupTagFilterReturn {
  const [filterState, setFilterState] = useState<FilterState>(() => ({
    value: initialFilters,
    source: initialFilters,
    hasLocalChange: false,
  }));
  // Props win whenever the parent supplies a genuinely changed value. An
  // equal-but-new array is ignored until a local edit has occurred, at which
  // point it is the parent's acknowledgement (or rejection) of that edit.
  // Deriving this during render avoids the old effect -> setState render pass.
  const activeFilters = resolveActiveFilters(filterState, initialFilters);
  // The mutations below chain off this rather than off the render-scoped
  // `activeFilters`: two toggles dispatched in the same batch both close over
  // the same render, so the second would compute from the pre-batch set and
  // drop the first's change (locally and in the value handed to the parent).
  // Each update writes what it produced (from an event handler, never during
  // render); the effect re-syncs it to whatever the resolver settled on, which
  // has run by the time any handler can fire, so the parent's value still wins.
  const latestFiltersRef = useRef(activeFilters);
  useEffect(() => {
    latestFiltersRef.current = activeFilters;
  }, [activeFilters]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Notify parent when filters change
  const updateFilters = useCallback(
    (newFilters: string[]) => {
      latestFiltersRef.current = newFilters;
      setFilterState({
        value: newFilters,
        source: initialFilters,
        hasLocalChange: true,
      });
      onFilterChange?.(newFilters);
    },
    [initialFilters, onFilterChange]
  );

  // Build a map of tag ID -> tag for efficient lookup (used for parent-child expansion)
  // NOTE: Must be defined before toggleFilter/removeFilter which depend on it
  const tagById = useMemo(() => {
    const map = new Map<string, { id: string; childTagIds?: string[]; parentTagId?: string }>();
    tagGroups.forEach((group) => {
      group.tags.forEach((tag) => {
        map.set(tag.id, {
          id: tag.id,
          childTagIds: tag.childTagIds,
          parentTagId: tag.parentTagId,
        });
      });
    });
    return map;
  }, [tagGroups]);

  // Toggle a filter on/off
  // For filter groups (filter-group-{id}), toggle all children but NOT the parent ID
  // Parent checkbox state is derived from children in the UI
  const toggleFilter = useCallback(
    (filterId: string) => {
      const current = latestFiltersRef.current;
      const tag = tagById.get(filterId);

      // Check if this is a parent tag with children (filter group)
      const isFilterGroupParent = isDerivedParentTagId(filterId) && tag?.childTagIds?.length;

      if (isFilterGroupParent && tag?.childTagIds) {
        // For filter groups: toggle children only (never add parent ID to filters)
        // Check if ALL children are currently selected
        const allChildrenSelected = tag.childTagIds.every((id) => current.includes(id));

        if (allChildrenSelected) {
          // Deselecting: remove all children (parent was "checked" via children)
          const childSet = new Set(tag.childTagIds);
          updateFilters(current.filter((id) => !childSet.has(id)));
          return;
        }

        // Selecting: add all children (parent will appear checked via children)
        const newFilters = new Set(current);
        tag.childTagIds.forEach((id) => newFilters.add(id));
        updateFilters(Array.from(newFilters));
        return;
      }

      // For regular filters or individual items: just toggle this one
      updateFilters(
        current.includes(filterId)
          ? current.filter((id) => id !== filterId)
          : [...current, filterId]
      );
    },
    [tagById, updateFilters]
  );

  // Remove a specific filter
  // For filter groups (filter-group-{id}), remove all children (parent ID should not be in filters)
  const removeFilter = useCallback(
    (filterId: string) => {
      const current = latestFiltersRef.current;
      const tag = tagById.get(filterId);

      // Check if this is a parent tag with children (filter group)
      const isFilterGroupParent = isDerivedParentTagId(filterId) && tag?.childTagIds?.length;

      if (isFilterGroupParent && tag?.childTagIds) {
        // For filter groups: remove all children (parent ID should not be in filters,
        // but remove it too for backward compatibility with old URL state)
        const allRelatedIds = new Set([filterId, ...tag.childTagIds]);
        updateFilters(current.filter((id) => !allRelatedIds.has(id)));
        return;
      }

      // For regular filters or individual items: just remove this one
      updateFilters(current.filter((id) => id !== filterId));
    },
    [tagById, updateFilters]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    updateFilters([]);
  }, [updateFilters]);

  // Build a map of group ID -> tag IDs for efficient lookup
  const tagIdsByGroup = useMemo(() => {
    const map = new Map<string, Set<string>>();
    tagGroups.forEach((group) => {
      const tagIds = new Set(group.tags.map((tag) => tag.id));
      map.set(group.id, tagIds);
    });
    return map;
  }, [tagGroups]);

  // Get selected tags for a specific group
  const getSelectedTagsForGroup = useCallback(
    (groupId: string): string[] => {
      const groupTagIds = tagIdsByGroup.get(groupId);
      if (!groupTagIds) return [];
      return activeFilters.filter((id) => groupTagIds.has(id));
    },
    [activeFilters, tagIdsByGroup]
  );

  // Get count of selected tags for a specific group
  const getSelectedCountForGroup = useCallback(
    (groupId: string): number => {
      return getSelectedTagsForGroup(groupId).length;
    },
    [getSelectedTagsForGroup]
  );

  return {
    // State
    activeFilters,
    openDropdown,

    // Actions
    toggleFilter,
    removeFilter,
    clearAllFilters,
    setFilters: updateFilters,
    setOpenDropdown,

    // Computed
    getSelectedTagsForGroup,
    getSelectedCountForGroup,
  };
}
